(() => {
  const SUCCESS_MESSAGE = 'Votre commande a bien été envoyée. Nous vous contacterons pour la confirmer.';

  const CHECKOUT_SELECTION_KEY = 'wearse-store-checkout-selection-v1';
  const CHECKOUT_SELECTION_MAX_AGE = 30 * 60 * 1000;

  function trim(value) {
    return String(value || '').trim();
  }

  function parseJson(value, fallback) {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  }

  function readCheckoutSelection() {
    try {
      const selection = parseJson(sessionStorage.getItem(CHECKOUT_SELECTION_KEY), null);
      const isFresh = Date.now() - Number(selection?.created_at || 0) <= CHECKOUT_SELECTION_MAX_AGE;
      if (selection?.version !== 1 || !isFresh || !Array.isArray(selection.lines) || !selection.lines.length) {
        sessionStorage.removeItem(CHECKOUT_SELECTION_KEY);
        return null;
      }
      return selection;
    } catch (_error) {
      return null;
    }
  }

  function clearCheckoutSelection() {
    try {
      sessionStorage.removeItem(CHECKOUT_SELECTION_KEY);
    } catch (_error) {
      // Storage can be unavailable in privacy modes.
    }
  }

  function formatMoney(cents, currency) {
    const amount = (Math.max(0, Number(cents) || 0) / 100).toFixed(2);
    return currency === 'MAD' ? `Dh ${amount} MAD` : `${amount} ${currency}`;
  }

  function applyCheckoutSelection(summary) {
    const selection = readCheckoutSelection();
    if (!selection || !summary) return false;

    const availableLines = Array.from(summary.querySelectorAll('[data-wearse-order-line]'));
    const selectedLines = new Set();
    let selectedQuantity = 0;
    let selectedTotal = 0;

    for (const requestedLine of selection.lines) {
      const requestedKey = trim(requestedLine.line_key);
      let line = availableLines.find(
        (candidate) => !selectedLines.has(candidate) && trim(candidate.dataset.lineKey) === requestedKey,
      );

      if (!line) {
        const sameVariant = availableLines.filter(
          (candidate) =>
            !selectedLines.has(candidate) && String(candidate.dataset.variantId) === String(requestedLine.variant_id),
        );
        if (sameVariant.length === 1) line = sameVariant[0];
      }

      if (!line) continue;

      const currentQuantity = Math.max(0, Number(line.dataset.quantity) || 0);
      const quantity = Math.min(currentQuantity, Math.max(0, Number(requestedLine.quantity) || 0));
      if (!quantity) continue;

      const unitPrice = Math.max(0, Number(line.dataset.unitPrice) || 0);
      const lineTotal = unitPrice * quantity;
      line.dataset.quantity = String(quantity);
      line.dataset.linePrice = String(lineTotal);

      const quantityLabel = line.querySelector('[data-wearse-order-line-quantity], .wearse-order-line__content > p');
      if (quantityLabel) quantityLabel.textContent = `Quantit\u00e9 : ${quantity}`;
      const lineTotalLabel = line.querySelector('[data-wearse-order-line-total], .wearse-order-line__price');
      if (lineTotalLabel) lineTotalLabel.textContent = formatMoney(lineTotal, summary.dataset.currency || 'MAD');

      selectedLines.add(line);
      selectedQuantity += quantity;
      selectedTotal += lineTotal;
    }

    for (const line of availableLines) {
      if (!selectedLines.has(line)) line.remove();
    }

    const totalLabel = formatMoney(selectedTotal, summary.dataset.currency || 'MAD');
    summary.dataset.itemCount = String(selectedQuantity);
    summary.dataset.totalPrice = String(selectedTotal);
    summary.dataset.totalPriceLabel = totalLabel;
    const subtotal = summary.querySelector(
      '[data-wearse-order-subtotal], .wearse-order-summary__totals div:first-child strong',
    );
    if (subtotal) subtotal.textContent = totalLabel;
    return true;
  }

  function readCart(container) {
    return Array.from(container.querySelectorAll('[data-wearse-order-line]')).map((line) => ({
      line_key: line.dataset.lineKey,
      product_id: line.dataset.productId,
      variant_id: line.dataset.variantId,
      product_title: line.dataset.productTitle,
      variant_title: line.dataset.variantTitle,
      product_url: line.dataset.productUrl,
      options: parseJson(line.dataset.options || '[]', []),
      quantity: Number(line.dataset.quantity || 0),
      unit_price: line.dataset.unitPrice,
      line_price: line.dataset.linePrice,
      currency: line.dataset.currency,
    }));
  }

  function validateOrderForm(form) {
    const errors = [];
    const name = form.elements.customer_name;
    const phone = form.elements.customer_phone;
    const email = form.elements.customer_email;
    const city = form.elements.customer_city;
    const address = form.elements.shipping_address;

    if (!trim(name?.value)) errors.push('Nom complet obligatoire.');
    if (!trim(phone?.value)) errors.push('Téléphone obligatoire.');
    if (!trim(city?.value)) errors.push('Ville obligatoire.');
    if (!trim(address?.value)) errors.push('Adresse de livraison obligatoire.');
    if (trim(email?.value) && !email.checkValidity()) errors.push('Adresse e-mail invalide.');

    return errors;
  }

  function getOrderNumber(form) {
    if (form.dataset.wearseOrderNumber) return form.dataset.wearseOrderNumber;

    const now = new Date();
    const date = now.toISOString().slice(0, 10).replaceAll('-', '');
    const time = now.toTimeString().slice(0, 8).replaceAll(':', '');
    const random = globalThis.crypto?.randomUUID
      ? globalThis.crypto.randomUUID().slice(0, 6).toUpperCase()
      : Math.random().toString(36).slice(2, 8).toUpperCase();

    form.dataset.wearseOrderNumber = `WEARSE-${date}-${time}-${random}`;
    return form.dataset.wearseOrderNumber;
  }

  function getOptionSummary(lines, acceptedNames) {
    return lines
      .map((line) => {
        const option = line.options.find((item) => acceptedNames.includes(trim(item.name).toLowerCase()));
        return option?.value ? `${line.product_title}: ${option.value}` : '';
      })
      .filter(Boolean)
      .join(' | ');
  }

  function getProductsSummary(lines) {
    return lines
      .map((line) => {
        const variant = trim(line.variant_title);
        const variantLabel = variant && variant.toLowerCase() !== 'default title' ? ` - ${variant}` : '';
        return `${trim(line.product_title) || 'Produit'}${variantLabel} x${line.quantity}`;
      })
      .join(' | ');
  }

  function buildOrderData(form, cartLines, summary) {
    const name = trim(form.elements.customer_name?.value);
    const phone = trim(form.elements.customer_phone?.value);
    const email = trim(form.elements.customer_email?.value);
    const city = trim(form.elements.customer_city?.value);
    const address = trim(form.elements.shipping_address?.value);
    const message = trim(form.elements.customer_message?.value);
    const itemCount = Number(summary?.dataset.itemCount || 0);
    const totalPriceLabel = trim(summary?.dataset.totalPriceLabel);
    const products = getProductsSummary(cartLines);
    const orderNumber = getOrderNumber(form);

    return {
      orderNumber,
      request_id: orderNumber,
      landing: 'store',
      landing_page: 'store',
      sheet_name: 'Store commandes',
      sheet: 'Store commandes',
      worksheet: 'Store commandes',
      submitted_at: new Date().toISOString(),
      dateLabel: new Date().toLocaleString('fr-MA'),
      fullName: name,
      full_name: name,
      phone,
      city,
      address,
      email: email || null,
      message: message || null,
      customer_email: email || null,
      customer_message: message || null,
      payment_method: 'cash_on_delivery',
      offer: `${itemCount} article(s)`,
      price: totalPriceLabel,
      products,
      products_summary: products,
      color: getOptionSummary(cartLines, ['couleur', 'color']),
      size: getOptionSummary(cartLines, ['taille', 'size', 'pointure']),
      item_count: itemCount,
      total_quantity: itemCount,
      total_price: summary?.dataset.totalPrice || '',
      total_price_label: totalPriceLabel,
      currency: summary?.dataset.currency || cartLines[0]?.currency || 'MAD',
      source: 'Boutique Shopify',
      source_url: window.location.href,
      userAgent: navigator.userAgent,
      items_json: JSON.stringify(cartLines),
      lines: cartLines,
      customer: {
        name,
        phone,
        email: email || null,
        city,
        address,
        message: message || null,
      },
    };
  }

  async function sendOrder(payload, endpoint) {
    if (!endpoint) return { configured: false, ok: false };

    const response = await fetch(endpoint, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8',
      },
      body: JSON.stringify(payload),
    });

    return {
      configured: true,
      ok: response.type === 'opaque' || response.ok,
    };
  }

  function getCartRoute(path) {
    const root = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    return `${root}${path.replace(/^\//, '')}`;
  }

  function getOrderedLineUpdates(cart, orderedLines) {
    const updates = {};
    const remainingByKey = new Map(cart.items.map((item) => [String(item.key), Number(item.quantity)]));
    let removedQuantity = 0;

    for (const orderedLine of orderedLines) {
      const requestedKey = trim(orderedLine.line_key);
      let cartLine = cart.items.find((item) => String(item.key) === requestedKey);

      if (!cartLine) {
        const sameVariant = cart.items.filter(
          (item) =>
            String(item.variant_id) === String(orderedLine.variant_id) &&
            !Object.prototype.hasOwnProperty.call(updates, item.key),
        );
        if (sameVariant.length === 1) cartLine = sameVariant[0];
      }

      if (!cartLine) continue;

      const key = String(cartLine.key);
      const currentQuantity = remainingByKey.get(key) ?? Number(cartLine.quantity);
      const quantityToRemove = Math.min(currentQuantity, Math.max(0, Number(orderedLine.quantity) || 0));
      const remainingQuantity = currentQuantity - quantityToRemove;
      remainingByKey.set(key, remainingQuantity);
      updates[key] = remainingQuantity;
      removedQuantity += quantityToRemove;
    }

    return {
      updates,
      expectedItemCount: Math.max(0, Number(cart.item_count) - removedQuantity),
    };
  }

  async function removeOrderedItems(orderedLines) {
    const currentCartResponse = await fetch(getCartRoute('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!currentCartResponse.ok) throw new Error(`Cart read failed with status ${currentCartResponse.status}`);

    const currentCart = await currentCartResponse.json();
    const { updates, expectedItemCount } = getOrderedLineUpdates(currentCart, orderedLines);
    if (!Object.keys(updates).length) return currentCart;

    let lastError;

    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await fetch(getCartRoute('cart/update.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            updates,
            sections: ['cart-icon-bubble', 'cart-drawer'],
            sections_url: `${window.location.pathname}${window.location.search}`,
          }),
        });

        if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);

        const cart = await response.json();
        if (Number(cart.item_count) !== expectedItemCount) throw new Error('Ordered quantities were not removed');
        return cart;
      } catch (error) {
        lastError = error;
        if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
      }
    }

    throw lastError || new Error('Cart update failed');
  }

  function replaceCartSection(selector, sectionHtml, sourceSelector) {
    const target = document.querySelector(selector);
    if (!target || !sectionHtml) return;

    const source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(sourceSelector);
    if (!source) return;

    if (selector === '#cart-icon-bubble') target.replaceChildren(...source.childNodes);
    else target.replaceWith(source);
  }

  function renderUpdatedCart(cart, summary) {
    replaceCartSection('#cart-icon-bubble', cart.sections?.['cart-icon-bubble'], '.shopify-section');
    replaceCartSection('cart-drawer', cart.sections?.['cart-drawer'], 'cart-drawer');

    document.querySelectorAll('[data-wearse-menu-cart] .cart-count-bubble').forEach((bubble) => {
      if (Number(cart.item_count) === 0) {
        bubble.remove();
        return;
      }

      const visibleCount = bubble.querySelector('[aria-hidden="true"]');
      if (visibleCount) visibleCount.textContent = String(cart.item_count);
      const accessibleCount = bubble.querySelector('.visually-hidden');
      if (accessibleCount) accessibleCount.textContent = `${cart.item_count} article(s)`;
    });

    if (summary) {
      summary.dataset.itemCount = '0';
      summary.dataset.totalPrice = '0';
      summary.dataset.totalPriceLabel = 'Dh 0.00 MAD';

      const items = summary.querySelector('.wearse-order-summary__items');
      if (items) {
        const emptyMessage = document.createElement('p');
        emptyMessage.className = 'wearse-order-summary__note';
        emptyMessage.textContent = 'Les articles command\u00e9s ont \u00e9t\u00e9 retir\u00e9s du panier.';
        items.replaceChildren(emptyMessage);
      }

      const subtotal = summary.querySelector('.wearse-order-summary__totals div:first-child strong');
      if (subtotal) subtotal.textContent = 'Dh 0.00 MAD';

      const cartLink = summary.querySelector('.wearse-order-summary__header a');
      if (cartLink) {
        cartLink.href = Number(cart.item_count) > 0 ? getCartRoute('cart') : '/pages/categories';
        cartLink.textContent = Number(cart.item_count) > 0 ? 'Voir les articles restants' : 'Continuer les achats';
      }
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wearse-order', cartData: cart });
    }
  }

  function setSubmitting(button, submitting) {
    if (!button) return;

    if (!button.dataset.originalText) button.dataset.originalText = trim(button.textContent);
    button.disabled = submitting;
    button.textContent = submitting ? 'Envoi en cours...' : button.dataset.originalText;
  }

  document.querySelectorAll('[data-wearse-order-form]').forEach((form) => {
    let completed = false;
    const summary = document.querySelector('[data-wearse-order-summary]');
    applyCheckoutSelection(summary);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (completed) return;

      const endpoint = trim(form.dataset.wearseEndpoint);
      const submitButton = form.querySelector('[type="submit"]');
      const errorsTarget = form.querySelector('[data-wearse-order-errors]');
      const messageTarget = form.querySelector('[data-wearse-order-message]');
      const cartLines = summary ? readCart(summary) : [];
      const errors = validateOrderForm(form);

      if (!cartLines.length) errors.push('Votre panier est vide.');
      if (!endpoint) errors.push("L'envoi de la commande n'est pas encore configuré.");

      if (errors.length) {
        if (errorsTarget) {
          errorsTarget.innerHTML = `<ul>${errors.map((error) => `<li>${error}</li>`).join('')}</ul>`;
          errorsTarget.hidden = false;
        }
        if (messageTarget) messageTarget.hidden = true;
        return;
      }

      if (errorsTarget) errorsTarget.hidden = true;
      setSubmitting(submitButton, true);

      try {
        const payload = buildOrderData(form, cartLines, summary);
        const result = await sendOrder(payload, endpoint);

        if (!result.ok) throw new Error('Order submission failed');

        completed = true;
        let cartUpdated = false;

        try {
          const cart = await removeOrderedItems(cartLines);
          renderUpdatedCart(cart, summary);
          cartUpdated = true;
        } catch (error) {
          console.error('WEARSE ordered item removal failed', error);
        }

        clearCheckoutSelection();

        form.classList.add('is-submitted');

        if (messageTarget) {
          messageTarget.textContent = cartUpdated
            ? SUCCESS_MESSAGE
            : `${SUCCESS_MESSAGE} Rechargez la page pour mettre le panier a jour.`;
          messageTarget.hidden = false;
          messageTarget.focus?.();
        }

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Commande envoyée';
        }
      } catch (_error) {
        if (errorsTarget) {
          errorsTarget.innerHTML = "<ul><li>Impossible d'envoyer la commande. Veuillez réessayer.</li></ul>";
          errorsTarget.hidden = false;
        }
        if (messageTarget) messageTarget.hidden = true;
        setSubmitting(submitButton, false);
      }
    });
  });

  window.WearseOrder = {
    validateOrderForm,
    buildOrderData,
    sendOrder,
    readCheckoutSelection,
    applyCheckoutSelection,
    getOrderedLineUpdates,
    removeOrderedItems,
    renderUpdatedCart,
  };
})();
